// Home's widgets (DS #65; DC-43, DC-48, DC-52, DC-54; V1-INTERACTIONS §4.26). `home.open` is the authority: it sends
// only the widgets the union of the person's roles grants, each once, in the fixed slot order, and leaves out a widget
// with nothing to say (the breach alert at zero) or no data yet (a later lane's). This file draws each widget it knows
// by its registry key and draws nothing for a key it does not know — there is no role table here, and no placeholder.
// A later lane draws its own keys through `registerHomeWidget(key, view)`, consulted after the views here (D-6, HO-18);
// `missingViews` names a key with no view, so a key `home.open` sends and nobody draws fails its test.
//
// The breach alert is the red banner of DC-48 above the grid; every other widget is a design-system Widget card.
// Rows reuse the Action Center's row, so a preview row and its Action Center row are the same link. The HM's *Own open
// reqs* draw each requisition on B10's requisition card (D-25, DC-53, HO-21: B10's card for every seat and the HM's own
// requisitions on the same card): Req health and Search Readiness, the status, the compact station strip, the counts per
// board column and the foot line with its Days open as Calendar Time, Active Time and Days on Hold (HO-20); under the
// foot, the drivers that cost points and the missing prerequisites as deep-linked rows (D-6, HO-22: "Health always shows
// drivers"; a clear driver stays inside the Req health name and on the requisition's own card). With no rows it draws
// the empty sentence the work lists draw (F-75).
//
// D-9 (HO-19): the Compensation queue draws the Compensation review chain (Pending -> In Review -> Finalized) as its
// counts, then the reviews as rows, each a link to its worksheet; the Coordinator Workbench draws a count per §4.2 status,
// each a link into the Workbench on that status, then the open requests at scheduling SLA risk, each a link to its
// request, under *Scheduling SLA risk* with its count, which opens the Workbench on the risk filter.

import { cloneElement, type ReactElement, type ReactNode } from "react";
import {
  ActionRows, Banner, COMPENSATION_REVIEW_STATUS, CORE_LABELS, CheckRow, Checklist, Count, Due, EmptyState, Flex, LEVEL_DISPLAY,
  METRIC_LABELS, NAV_ITEM_LABELS, Row, RowName, SEATS, SLA_TIER_LABELS, SURFACE_LABELS, SectionTitle, Stack, Stat, TextLink,
  WIDGET_LABELS, Widget, Widgets, Pill, formatDateTime,
  type HealthBand, type HealthDriver, type SeatKey, type WidgetKind,
} from "@/mc";
import type { ActionRow as ActionItemRow, DriverOut, PrerequisiteOut, WidgetOut } from "@/api/tools";
import { ActionCenterRow } from "@/views/actions/ActionRow";
import { RequisitionCard, daysLine, stationsOf } from "@/views/b10/home/ActiveRequisitions";
import type { ActiveRequisitionRow } from "@/views/b10/home/api";
import { HOME_LABELS } from "@/views/home/labels";
import { driversOf, prerequisitesOf } from "@/views/requisition/SpineCards";
import { STATUS_WORD as SCHEDULING_STATUS_WORD, dueTier, templateWord } from "@/views/scheduling/workbench";

/** The server's slot names (app/dashboard.py WIDGET_SLOTS) as the grid's kinds (DS #65 order). */
const KIND_OF_SLOT: Readonly<Record<string, WidgetKind>> = {
  alert: "alert",
  kpis: "kpis",
  action_center: "actionCenter",
  sections: "sections",
  health_readiness: "health",
  timelines: "timelines",
  reports: "reports",
};

/**
 * The seats a widget is drawn for, written onto data-roles for the capture harness and the walks to read. It mirrors
 * app/dashboard.py WIDGETS and decides nothing: the server has already chosen the widgets.
 */
const ROLES_OF_WIDGET: Readonly<Record<string, readonly SeatKey[]>> = {
  breach_alert: SEATS,
  action_center_preview: SEATS,
  own_open_reqs: ["hm"],
  assigned_interviewer_work: ["hm"],
  assigned_list: ["interviewer"],
  comp_queue: ["compensation"],
  workbench: ["coordinator"],
};

interface BreachAlert {
  count: number;
  route: string;
}
interface Preview {
  rows: ActionItemRow[];
  open: number;
  glow: boolean;
}
interface OwnReq {
  requisition_id: string;
  title: string;
  status: string;
  health_score: number | null;
  health_light: string | null;
  readiness_percent: number | null;
  route: string;
  /** Days open: Calendar Time from the opening, Active Time without the holds, Days on Hold (null before it opens). */
  calendar_days?: number | null;
  active_days?: number | null;
  hold_days?: number | null;
  /** Every driver of the stored health, as health.open sends them. */
  drivers?: DriverOut[];
  /** The fed prerequisites not yet met, as readiness.open sends them. */
  missing_prerequisites?: PrerequisiteOut[];
  /** The applications in each of his board columns (DC-31): counts, no candidate. */
  stage_counts?: Record<string, number>;
  /** DC-30's stations as the server reads them from the record: [{key, state}]. */
  stations?: { key: string; state: string }[];
}
interface Rows<T> {
  rows: T[];
}
/** One review as `comp_review.queue` lists it: ids, status and the job, never a figure. */
interface CompReview {
  review_id: string;
  request_id: string;
  status: string;
  title: string;
  level: string;
  route: string;
}
interface CompStatus {
  status: string;
  count: number;
  rows: CompReview[];
}
interface CompQueue {
  route: string;
  total: number;
  statuses: CompStatus[];
}
/** One open request at scheduling SLA risk, as `scheduling.workbench` lists it. */
interface RiskRow {
  key: string;
  requisition_id: string;
  meeting_type: string;
  status: string;
  risk: string;
  due_at: string | null;
  event_tz: string | null;
  candidate_display_name: string | null;
  route: string;
}
interface WorkbenchCounts {
  route: string;
  total: number;
  statuses: { status: string; count: number; route: string }[];
  risk: { count: number; route: string; rows: RiskRow[] };
}

const isBand = (light: string | null): light is HealthBand => light === "green" || light === "yellow" || light === "red";

function rowsOf<T>(widget: WidgetOut): T[] {
  const rows = (widget.data as unknown as Partial<Rows<T>>).rows;
  return Array.isArray(rows) ? rows : [];
}

/** DC-48: the dashboard alert, breach tier only; its link opens the Action Center on the breached items. */
export function BreachAlert({ widget }: { widget: WidgetOut }) {
  const data = widget.data as unknown as BreachAlert;
  return (
    <div data-widget={widget.key} data-kind="alert">
      <Banner
        tone="red"
        live
        right={<TextLink href={data.route} after="right" >{NAV_ITEM_LABELS.actionCenter}</TextLink>}
      >
        {SLA_TIER_LABELS.breach} <Count n={data.count} />
      </Banner>
    </div>
  );
}

/**
 * A list of action rows. The preview's rows are home.ac.row.<i>; a work list's are home.work.row.<i> (a person sees at
 * most one work list: the Interviewer-only list is never shown beside the HM's sections).
 */
function ActionList({ rows, preview }: { rows: readonly ActionItemRow[]; preview?: boolean | undefined }) {
  return (
    <ActionRows>
      {rows.map((row, index) => (
        <ActionCenterRow key={row.id} row={row} hs={preview ? `home.ac.row.${index}` : `home.work.row.${index}`} />
      ))}
    </ActionRows>
  );
}

function previewWidget(widget: WidgetOut): ReactElement {
  const data = widget.data as unknown as Preview;
  return (
    <Widget
      key={widget.key}
      id={widget.key}
      kind="actionCenter"
      roles={ROLES_OF_WIDGET[widget.key] ?? []}
      title={METRIC_LABELS.actionCenter}
      count={data.open}
      tone={data.glow ? "glow" : undefined}
      level={2}
      actions={<TextLink href="#action-center" after="right" >{METRIC_LABELS.prioritizedActions}</TextLink>}
      flush
    >
      <ActionList rows={rowsOf<ActionItemRow>(widget)} preview />
    </Widget>
  );
}

/** The card's foot line under a requisition: "Days open {calendar} · Active Time {active} · Days on Hold {hold}" (B10's
 * requisition card draws it). */
export { daysLine };

/** Points as the score moved: "−15", "+5", "0" (the Req health card's words). */
const signed = (points: number): string => points < 0 ? `−${Math.abs(points)}` : points > 0 ? `+${points}` : "0";

/** A driver's line as the Req health card draws it: what it measured and the points it moved the score ("2 · −10"). */
function driverLine({ value, points }: HealthDriver): string | undefined {
  const parts = [value, points === undefined ? undefined : signed(points)].filter((part): part is string => !!part);
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

/** What the card lists under its foot: the drivers that cost points and the missing prerequisites, each a deep link to
 * the screen that clears it (the requisition card's words and links, D-7); nothing when none. */
function OwnReqChecks({ req, index }: { req: OwnReq; index: number }) {
  const drivers = driversOf(req.drivers ?? []).filter((driver) => driver.status !== "ok");
  const missing = prerequisitesOf(req.missing_prerequisites ?? []).filter((row) => row.status !== "ok");
  if (drivers.length + missing.length === 0) return null;
  return (
    <Checklist>
      {drivers.map((driver) => (
        <CheckRow key={driver.hs} status={driver.status} sub={driverLine(driver)} href={driver.href}
          hs={`home.req.${index}.health.${String(driver.hs).split(".").pop()}`}>{driver.text}</CheckRow>
      ))}
      {missing.map((row) => (
        <CheckRow key={row.hs} status={row.status} sub={row.sub} href={row.href}
          hs={`home.req.${index}.readiness.${String(row.hs).split(".").pop()}`}>{row.text}</CheckRow>
      ))}
    </Checklist>
  );
}

/** An own requisition as B10's card reads a row. */
function cardRow(req: OwnReq): ActiveRequisitionRow {
  return {
    requisition_id: req.requisition_id,
    title: req.title,
    status: req.status || null,
    health_score: req.health_score,
    health_light: isBand(req.health_light) ? req.health_light : null,
    readiness_percent: req.readiness_percent,
    stage_counts: req.stage_counts ?? {},
    route: req.route,
  };
}

function ownOpenReqs(widget: WidgetOut): ReactElement {
  const rows = rowsOf<OwnReq>(widget);
  return (
    <Widget
      key={widget.key}
      id={widget.key}
      kind="sections"
      roles={ROLES_OF_WIDGET[widget.key] ?? []}
      title={WIDGET_LABELS.ownOpenReqs}
      sub={SURFACE_LABELS.ownReqs}
      count={rows.length}
      level={2}
    >
      {rows.length === 0 ? <EmptyState>{CORE_LABELS.nothingNeedsAction}</EmptyState> : (
        <Stack gap={14}>
          {rows.map((req, index) => (
            <RequisitionCard
              key={req.requisition_id}
              row={cardRow(req)}
              drivers={driversOf(req.drivers ?? [])}
              stations={stationsOf(req.stations)}
              days={req}
              stages={req.stage_counts !== undefined}
              hsPrefix="home"
            >
              <OwnReqChecks req={req} index={index} />
            </RequisitionCard>
          ))}
        </Stack>
      )}
    </Widget>
  );
}

function workList(widget: WidgetOut, title: string, sub?: string): ReactElement {
  const rows = rowsOf<ActionItemRow>(widget);
  return (
    <Widget
      key={widget.key}
      id={widget.key}
      kind="sections"
      roles={ROLES_OF_WIDGET[widget.key] ?? []}
      title={title}
      sub={sub}
      count={rows.length}
      level={2}
      flush
    >
      <ActionList rows={rows} />
    </Widget>
  );
}

/** The Compensation review chain's words (G §5 "Compensation: Pending -> In Review -> Finalized."). */
const COMP_STATUS_WORD: Readonly<Record<string, string>> = {
  pending: COMPENSATION_REVIEW_STATUS.pending,
  in_review: COMPENSATION_REVIEW_STATUS.inReview,
  finalized: COMPENSATION_REVIEW_STATUS.finalized,
};

const list = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

/** D-9: the Compensation queue — the chain's counts, then each review, a link to its worksheet. */
function compQueue(widget: WidgetOut): ReactElement {
  const data = widget.data as unknown as Partial<CompQueue>;
  const statuses = list<CompStatus>(data.statuses);
  const rows = statuses.flatMap((group) => list<CompReview>(group.rows));
  return (
    <Widget
      key={widget.key}
      id={widget.key}
      kind="sections"
      roles={ROLES_OF_WIDGET[widget.key] ?? []}
      title={NAV_ITEM_LABELS.compensationQueue}
      count={data.total ?? rows.length}
      level={2}
    >
      <Stack gap={12}>
        <Flex gap={14} wrap>
          {statuses.map((group) => (
            <Stat key={group.status} label={COMP_STATUS_WORD[group.status] ?? group.status} value={group.count} />
          ))}
        </Flex>
        {rows.length === 0 ? <EmptyState>{CORE_LABELS.nothingNeedsAction}</EmptyState> : (
          <Stack gap={0}>
            {rows.map((row, index) => (
              <Row key={row.review_id} cols={2} lead="70%" href={row.route} >
                <RowName name={row.title} sub={[row.request_id, LEVEL_DISPLAY[row.level as keyof typeof LEVEL_DISPLAY] ?? row.level].join(" · ")} />
                <Pill>{COMP_STATUS_WORD[row.status] ?? row.status}</Pill>
              </Row>
            ))}
          </Stack>
        )}
      </Stack>
    </Widget>
  );
}

/** D-9: the Coordinator Workbench — a count per §4.2 status into the Workbench, then the scheduling SLA risk rows. */
function workbench(widget: WidgetOut): ReactElement {
  const data = widget.data as unknown as Partial<WorkbenchCounts>;
  const statuses = list<WorkbenchCounts["statuses"][number]>(data.statuses);
  const risk = data.risk ?? { count: 0, route: "#workbench?risk=sla", rows: [] };
  const rows = list<RiskRow>(risk.rows);
  return (
    <Widget
      key={widget.key}
      id={widget.key}
      kind="sections"
      roles={ROLES_OF_WIDGET[widget.key] ?? []}
      title={NAV_ITEM_LABELS.coordinatorWorkbench}
      count={data.total ?? 0}
      level={2}
    >
      <Stack gap={12}>
        <Flex gap={12} wrap>
          {statuses.map((group) => (
            <TextLink key={group.status} href={group.route} hs={`home.wb.status.${group.status.replace(/_/g, "-")}`}>
              <Pill>{(SCHEDULING_STATUS_WORD as Record<string, string>)[group.status] ?? group.status}</Pill>
              {" "}
              {group.count}
            </TextLink>
          ))}
        </Flex>
        <SectionTitle count={risk.count}>
          <TextLink href={risk.route} >{METRIC_LABELS.schedulingSlaRisk}</TextLink>
        </SectionTitle>
        {rows.length === 0 ? <EmptyState>{CORE_LABELS.nothingNeedsAction}</EmptyState> : (
          <Stack gap={0}>
            {rows.map((row, index) => (
              <Row key={row.key} cols={2} lead="60%" href={row.route} >
                <RowName name={templateWord(row.meeting_type)} sub={[row.candidate_display_name, row.requisition_id].filter(Boolean).join(" · ")} />
                {row.due_at
                  ? <Due tier={dueTier(row.risk)} phrase={formatDateTime(row.due_at, row.event_tz ?? undefined)} at={row.due_at} still />
                  : <Pill>{(SCHEDULING_STATUS_WORD as Record<string, string>)[row.status] ?? row.status}</Pill>}
              </Row>
            ))}
          </Stack>
        )}
      </Stack>
    </Widget>
  );
}

/**
 * A widget's view: an element builder, not a component (the grid keeps only Widget elements). It returns the
 * design-system Widget element for the server's widget, or null to leave it out; it calls no hook itself (a component
 * it draws inside the Widget may).
 */
export type HomeWidgetView = (widget: WidgetOut) => ReactElement | null;

/** Each widget Home draws itself, by its registry key. The breach alert is drawn above the grid, never in it. */
const WIDGET_VIEWS: Readonly<Record<string, HomeWidgetView>> = {
  action_center_preview: previewWidget,
  own_open_reqs: ownOpenReqs,
  assigned_interviewer_work: (widget) => workList(widget, WIDGET_LABELS.assignedInterviewerWorkElsewhere),
  // F-177: the Interviewer's interviews (records), headed in plain words — the Action Center widget holds the cards
  assigned_list: (widget) => workList(widget, NAV_ITEM_LABELS.interviews, SURFACE_LABELS.assigned),
  comp_queue: compQueue,
  workbench,
};
const ALERT_KEY = "breach_alert";

/** The views later lanes register, by key (from their own views/<lane>/home/register.ts). */
const REGISTERED = new Map<string, HomeWidgetView>();

/**
 * Registers the view of a widget key Home does not draw itself (HO-18: the lanes' widgets render once they merge).
 * Home's own views are consulted first, so a key Home draws, or one already registered, throws.
 */
export function registerHomeWidget(key: string, view: HomeWidgetView): void {
  if (key === ALERT_KEY || key in WIDGET_VIEWS) throw new Error(`registerHomeWidget: Home draws ${key} itself`);
  if (REGISTERED.has(key)) throw new Error(`registerHomeWidget: ${key} is already registered`);
  REGISTERED.set(key, view);
}

/** The view a widget key draws with: Home's own, else a registered one; undefined when none. */
export function homeWidgetView(key: string): HomeWidgetView | undefined {
  return WIDGET_VIEWS[key] ?? REGISTERED.get(key);
}

/** The keys of `keys` Home has no view for (a key `home.open` sends must have one). */
export function missingViews(keys: readonly string[]): string[] {
  return keys.filter((key) => key !== ALERT_KEY && homeWidgetView(key) === undefined);
}

/** The widgets in the server's order: the alert banner first, then the grid. */
export function HomeWidgets({ widgets }: { widgets: readonly WidgetOut[] }) {
  const alert = widgets.find((widget) => widget.key === ALERT_KEY);
  const cards: ReactNode[] = widgets
    .filter((widget) => widget.key !== ALERT_KEY && KIND_OF_SLOT[widget.slot] !== undefined)
    .map((widget) => {
      const card = homeWidgetView(widget.key)?.(widget) ?? null;
      return card !== null && card.key === null ? cloneElement(card, { key: widget.key }) : card;
    })
    .filter((card) => card !== null);
  return (
    <>
      {alert ? <BreachAlert widget={alert} /> : null}
      <Widgets>{cards}</Widgets>
    </>
  );
}
